import type { FreeformUnassociatedItem } from '../../utils/api-client/sse-schemas.ts'

// Association is a judgment the backend agent makes, not a string match, so a scan can land on the
// wrong repository. Below this the UI says so: the agent's own confidence is the only signal a user
// has that a scan is worth double-checking before acting on its fixes.
export const LOW_CONFIDENCE_THRESHOLD = 0.6

export type ReasonCopy = {
  label: string
  description: string
}

// Human copy for each machine-readable outcome. The vocabulary is the union of the analysis-service
// association taxonomy and the platform's scan-creation outcomes; an unrecognized reason falls back
// to a neutral bucket so a new backend value degrades to "logged for review" rather than a blank.
// The router framing matters: an unassociated finding was NOT judged safe — it just could not be
// turned into a scan — so the copy avoids "already fixed"/"not a problem" language.
//
// Listed in display order: the outcomes a user can act on (connect a repo, check a runtime setting)
// come before the operational ones. Reasons not listed here (including unknown ones) sort after, by
// first appearance, so the ordering is always stable.
const UNASSOCIATED_REASONS: readonly ({ reason: string } & ReasonCopy)[] = [
  {
    reason: 'not_code',
    label: 'No application code to scan',
    description: 'No source-code location — a runtime header, TLS/proxy/deploy setting, or a third-party console.',
  },
  {
    reason: 'repo_not_onboarded',
    label: 'Repository not connected',
    description: "The affected code is in a repository that isn't connected to Pixee.",
  },
  {
    reason: 'ambiguous',
    label: 'Repository could not be confirmed',
    description: 'Several connected repositories matched and none could be confirmed as the right one.',
  },
  {
    reason: 'file_not_located',
    label: 'Code could not be confirmed',
    description: 'Pixee matched a repository, but no location it found in it held the reported code.',
  },
  {
    reason: 'insufficient_information',
    label: 'Could not be located',
    description: 'Not enough detail to locate this finding in any connected repository.',
  },
  {
    reason: 'repository_no_longer_exists',
    label: 'Repository unavailable',
    description: 'Pixee matched a repository, but it no longer exists.',
  },
  {
    reason: 'default_branch_head_unresolved',
    label: 'Branch unavailable',
    description: "Pixee matched a repository, but its default branch couldn't be resolved.",
  },
  {
    reason: 'scan_creation_failed',
    label: "Scan couldn't be created",
    description: 'Pixee matched a repository but could not create a scan for it.',
  },
  {
    reason: 'association_failed',
    label: 'Processing error',
    description: "Matching didn't finish for this finding; it was logged so nothing is silently dropped.",
  },
]

const REASON_COPY = new Map(UNASSOCIATED_REASONS.map(({ reason, ...copy }) => [reason, copy]))

const REASON_RANK = new Map(UNASSOCIATED_REASONS.map(({ reason }, index) => [reason, index]))

const FALLBACK_REASON_COPY: ReasonCopy = {
  label: 'Logged for review',
  description: 'Pixee could not turn this finding into a scan.',
}

export const describeReason = (reason: string) => REASON_COPY.get(reason) ?? FALLBACK_REASON_COPY

export type UnassociatedGroup<T extends FreeformUnassociatedItem = FreeformUnassociatedItem> = {
  reason: string
  copy: ReasonCopy
  findings: T[]
}

// Group the unassociated findings by outcome so the UI shows one section per reason rather than a
// flat list. Order follows UNASSOCIATED_REASONS first, then any remaining reasons by first appearance.
export const groupUnassociatedByReason = <T extends FreeformUnassociatedItem>(
  findings: T[]
): UnassociatedGroup<T>[] => {
  const byReason = new Map<string, T[]>()
  for (const finding of findings) {
    const existing = byReason.get(finding.reason)
    if (existing === undefined) {
      byReason.set(finding.reason, [finding])
    } else {
      existing.push(finding)
    }
  }

  const rank = (reason: string) => REASON_RANK.get(reason) ?? UNASSOCIATED_REASONS.length

  return [...byReason.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([reason, groupFindings]) => ({ reason, copy: describeReason(reason), findings: groupFindings }))
}
